import React from 'react';
import { useAppHeaderStyle } from '@low6-pickem/ui';
import { useIsMobile } from '@/hooks/use-mobile';

interface AppHeaderProps {
  className?: string;
  showShadow?: boolean;
  showBackButton?: boolean;
  onBackClick?: () => void;
  onInfoClick?: () => void;
  /**
   * Optional element rendered in the banner's top-right in place of the info
   * button — used by the three-dot overflow menu (DKPLAT-664). When provided it
   * takes precedence over `onInfoClick`, so the two never render together.
   */
  headerAction?: React.ReactNode;
  /**
   * The configured header logo. When absent, whether anything renders depends on
   * `fullNavigation` — see the note on `logoSrc` in the component body.
   */
  headerLogoUrl?: string | null;
  /**
   * Defers the default-logo fallback while a configured logo is still resolving, so the
   * wrong-brand default does not flash first (DKPLAT-446). Only meaningful when
   * `fullNavigation` is true, since that is the only case with a fallback to defer.
   */
  isLogoLoading?: boolean;
  /**
   * Whether the tenant runs Pick'em with full navigation (`enableFullNavigation`).
   *
   * Used here purely as the tenant discriminator for the default logo: it is ON for
   * SkyPicks and OFF for DraftKings, and `/logo.svg` is the SkyPicks wordmark. Passed in
   * rather than read from `useTenant()` because that hook throws outside a
   * TenantProvider, and this component is rendered (and tested) without one.
   */
  fullNavigation?: boolean;
  /**
   * When true, omit the default gradient and clouds overlay (DKPLAT-437) — only
   * configured background styling is shown. The logo has no default at all now, so
   * this says nothing about it.
   */
  suppressFallbacks?: boolean;
  /**
   * Whether the banner stays pinned to the top of the screen as the page scrolls.
   *
   * Defaults to true, which is how the banner has always behaved. On the contest screen
   * this is driven per contest from `style_config.header.anchored`, so a contest that
   * also carries in-feed ads can let the banner scroll away and give the questions the
   * room back.
   */
  anchored?: boolean;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  className = '',
  showShadow = false,
  showBackButton = false,
  onBackClick,
  onInfoClick,
  headerAction,
  headerLogoUrl,
  isLogoLoading = false,
  fullNavigation = false,
  suppressFallbacks = false,
  anchored = true,
}) => {
  const { headerBgGradientStart, headerBgGradientEnd, headerBgImageUrl, headerBgImageUrlMobile } =
    useAppHeaderStyle();
  const isMobile = useIsMobile();
  const activeBgImageUrl = (isMobile ? headerBgImageUrlMobile : undefined) ?? headerBgImageUrl;

  const DEFAULT_GRADIENT = 'linear-gradient(71deg, #011125 0%, #02295A 49.52%, #014296 100%)';

  const buildHeaderBackground = (): string | undefined => {
    const hasGradient = Boolean(headerBgGradientStart || headerBgGradientEnd);
    const gradient = hasGradient
      ? `linear-gradient(180deg, ${headerBgGradientStart ?? headerBgGradientEnd}, ${headerBgGradientEnd ?? headerBgGradientStart})`
      : suppressFallbacks
        ? undefined
        : DEFAULT_GRADIENT;

    if (activeBgImageUrl && gradient) {
      return `${gradient}, url(${activeBgImageUrl}) center / cover no-repeat`;
    }
    if (activeBgImageUrl) {
      return `url(${activeBgImageUrl}) center / cover no-repeat`;
    }
    return gradient;
  };

  const headerBackground = buildHeaderBackground();
  const headerStyle: React.CSSProperties = {
    aspectRatio: '1080 / 330',
    ...(headerBackground && { background: headerBackground }),
    ...(activeBgImageUrl && { backgroundBlendMode: 'normal, luminosity' }),
    ...(showShadow && { filter: 'drop-shadow(0 7.609px 4.348px rgba(0, 0, 0, 0.25))' }),
  };

  const showCloudsOverlay = !suppressFallbacks && !activeBgImageUrl;

  /*
   * A configured logo always renders. With none configured, fall back to `/logo.svg`
   * ONLY under full navigation.
   *
   * `/logo.svg` is the SKYPICKS wordmark. It used to render unconditionally whenever no
   * logo was configured (DKPLAT-461), which put SkyPicks branding on DraftKings headers —
   * the defect the client reported. Removing it outright then took the logo away from
   * SkyPicks too, where it was correct and expected.
   *
   * `enableFullNavigation` is the discriminator: on for SkyPicks, off for DK. So the
   * fallback is restored for the tenant it belongs to and stays gone for the one it does
   * not. `isLogoLoading` still defers it, so a configured logo never flashes the default
   * first (DKPLAT-446) — that only matters on the fallback path, which is why it is
   * checked here rather than against `headerLogoUrl`.
   */
  const logoSrc = headerLogoUrl || (fullNavigation && !isLogoLoading ? '/logo.svg' : undefined);

  return (
    <header
      data-test-id="app-header"
      data-test-anchored={anchored}
      className={`w-full mb-2 ${
        anchored ? 'sticky top-0 z-50' : 'relative'
      } overflow-hidden flex items-center justify-center sm:h-[180px] md:h-[160px] ${className}`}
      style={headerStyle}
    >
      {showCloudsOverlay && (
        <img
          src="/clouds.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none opacity-10"
        />
      )}

      {/* Back Button - Left Side */}
      {showBackButton && onBackClick && (
        <button
          onClick={onBackClick}
          className="absolute left-[10%] md:left-[5%] top-1/2 -translate-x-1/2 -translate-y-1/2 z-30 p-2 hover:opacity-80 transition-opacity"
          aria-label="Go back"
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.25 12H3.75"
              stroke="#F5F5F5"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.5 5.25L3.75 12L10.5 18.75"
              stroke="#F5F5F5"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {logoSrc && (
        <img
          src={logoSrc}
          alt="Contest logo"
          className="relative w-[35%] md:w-[25%] h-auto object-contain z-10"
          style={{ filter: 'drop-shadow(0 7.609px 4.348px rgba(0, 0, 0, 0.25))' }}
        />
      )}

      {/* Overflow menu (DKPLAT-664) takes the info button's slot when present. */}
      {headerAction && (
        <div className="absolute right-[10%] md:right-[5%] top-1/2 translate-x-1/2 -translate-y-1/2 z-30 p-2">
          {headerAction}
        </div>
      )}

      {/* Info Button - Right Side */}
      {!headerAction && onInfoClick && (
        <button
          onClick={onInfoClick}
          className="absolute right-[10%] md:right-[5%] top-1/2 translate-x-1/2 -translate-y-1/2 z-30 p-2 hover:opacity-80 transition-opacity"
          aria-label="Contest details"
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2.3125C14.5678 2.31745 17.029 3.33959 18.8447 5.15527C20.6604 6.97096 21.6825 9.43224 21.6875 12C21.6875 13.916 21.1191 15.7888 20.0547 17.3818C18.9902 18.9749 17.4772 20.217 15.707 20.9502C13.937 21.6833 11.9894 21.8747 10.1104 21.501C8.23116 21.1272 6.50521 20.2044 5.15039 18.8496C3.79557 17.4948 2.87282 15.7688 2.49902 13.8896C2.1253 12.0106 2.31668 10.063 3.0498 8.29297C3.78303 6.52281 5.02506 5.00979 6.61816 3.94531C8.11175 2.94735 9.85119 2.3856 11.6416 2.31934L12 2.3125ZM11.25 10.4375C11.0345 10.4375 10.8282 10.5234 10.6758 10.6758C10.5234 10.8282 10.4375 11.0345 10.4375 11.25C10.4375 11.4655 10.5234 11.6718 10.6758 11.8242C10.8133 11.9618 10.9949 12.0437 11.1875 12.0586V16.5C11.1875 16.7155 11.2734 16.9218 11.4258 17.0742C11.5782 17.2266 11.7845 17.3125 12 17.3125H12.75C12.9655 17.3125 13.1718 17.2266 13.3242 17.0742C13.4766 16.9218 13.5625 16.7155 13.5625 16.5C13.5625 16.2845 13.4766 16.0782 13.3242 15.9258C13.1867 15.7882 13.0051 15.7053 12.8125 15.6904V11.25C12.8125 11.0345 12.7266 10.8282 12.5742 10.6758C12.4218 10.5234 12.2155 10.4375 12 10.4375H11.25ZM11.8125 6.6875C11.4976 6.6875 11.1954 6.81246 10.9727 7.03516C10.75 7.25786 10.625 7.56006 10.625 7.875C10.625 8.10987 10.6947 8.33987 10.8252 8.53516C10.9557 8.73029 11.1415 8.88185 11.3584 8.97168C11.5753 9.06148 11.8137 9.08577 12.0439 9.04004C12.2743 8.99422 12.4863 8.88092 12.6523 8.71484C12.8184 8.54877 12.9317 8.3368 12.9775 8.10645C13.0233 7.87623 12.999 7.63776 12.9092 7.4209C12.8193 7.20403 12.6678 7.01817 12.4727 6.8877C12.2774 6.75721 12.0474 6.6875 11.8125 6.6875Z"
              fill="#F5F5F5"
              stroke="#F5F5F5"
              strokeWidth="0.125"
            />
          </svg>
        </button>
      )}
    </header>
  );
};

export default AppHeader;
